/**
 * Pulse — per-survey results dashboard.
 *
 * HR_ADMIN+ only. Renders for each question:
 *   - average rating (scale)
 *   - simple bar chart from the distribution dict (recharts)
 *   - text-answer list (capped to 50 by the API)
 *
 * Plus the survey-level engagement score and (if present) eNPS
 * promoter/passive/detractor breakdown.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { getResults, type QuestionAggregate, type SurveyResults } from "@/api/pulse";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { pageTransition } from "@/lib/utils";

export function PulseResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState<SurveyResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getResults(id)
      .then((r) => {
        if (!cancelled) setResults(r);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load results.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <Card className="border-danger/40 bg-danger/5">
        <p className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!results) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded bg-border/30 animate-pulse" />
        ))}
      </div>
    );
  }

  const responseRatePct = Math.round(results.response_rate * 100);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title={results.title}
        subtitle="Survey results — aggregated across all responses."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/pulse")}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Button>
        }
      />

      {/* Top-line metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <MetricCard
          label="Engagement"
          value={
            results.engagement_score !== null
              ? `${results.engagement_score.toFixed(0)}/100`
              : "—"
          }
          hint="0–100 scale, derived from 1–5 questions"
        />
        <MetricCard
          label="Response rate"
          value={`${responseRatePct}%`}
          hint={`${results.total_respondents} of ${results.eligible_employees} active employees`}
        />
        <MetricCard
          label={results.is_anonymous ? "Anonymous" : "Identified"}
          value={results.is_anonymous ? "Yes" : "No"}
          hint={
            results.is_anonymous
              ? "Responses are not linked to employees"
              : "Responses are linked to employee profiles"
          }
        />
      </div>

      {/* eNPS — only when the survey has an eNPS-shaped question */}
      {results.enps && (
        <Card className="mb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                eNPS Score
              </h3>
              <p className="text-3xl font-bold font-heading text-text-primary mt-1">
                {results.enps.score >= 0 ? "+" : ""}
                {results.enps.score.toFixed(0)}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <ENPSBucket
                label="Promoters"
                count={results.enps.promoters}
                total={results.enps.total}
                color="bg-success"
              />
              <ENPSBucket
                label="Passives"
                count={results.enps.passives}
                total={results.enps.total}
                color="bg-amber-400"
              />
              <ENPSBucket
                label="Detractors"
                count={results.enps.detractors}
                total={results.enps.total}
                color="bg-danger"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Per-question detail */}
      <div className="space-y-4">
        {results.questions.map((q, idx) => (
          <QuestionResultCard key={q.question_id} q={q} idx={idx + 1} />
        ))}
      </div>
    </motion.div>
  );
}


function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="text-2xl font-semibold font-heading text-text-primary mt-1">
        {value}
      </p>
      <p className="text-xs text-text-muted mt-1">{hint}</p>
    </Card>
  );
}


function ENPSBucket({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${color}`} />
        <span className="text-xs uppercase tracking-wide text-text-muted">
          {label}
        </span>
      </div>
      <p className="text-lg font-semibold font-heading text-text-primary mt-1">
        {count}
      </p>
      <p className="text-xs text-text-muted">{pct}%</p>
    </div>
  );
}


function QuestionResultCard({ q, idx }: { q: QuestionAggregate; idx: number }) {
  const isScale = q.question_type === "scale_1_5" || q.question_type === "scale_1_10";
  const data = Object.entries(q.distribution)
    .map(([k, v]) => ({ rating: k, count: v }))
    .sort((a, b) => parseInt(a.rating, 10) - parseInt(b.rating, 10));

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-sm font-medium text-text-primary">
            {idx}. {q.question_text}
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {q.response_count} response{q.response_count === 1 ? "" : "s"}
          </p>
        </div>
        {isScale && q.average !== null && (
          <Badge variant="info">Avg {q.average.toFixed(2)}</Badge>
        )}
      </div>

      {isScale && data.length > 0 && <ScaleDistribution data={data} />}

      {q.question_type === "multiple_choice" && Object.keys(q.distribution).length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {Object.entries(q.distribution).map(([opt, n]) => (
            <li key={opt} className="flex items-center justify-between text-sm">
              <span className="text-text-primary">{opt}</span>
              <span className="text-text-muted">{n}</span>
            </li>
          ))}
        </ul>
      )}

      {q.question_type === "text" && q.text_answers.length > 0 && (
        <ul className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
          {q.text_answers.map((t, i) => (
            <li
              key={i}
              className="rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-text-primary"
            >
              {t}
            </li>
          ))}
        </ul>
      )}

      {q.response_count === 0 && (
        <p className="text-xs text-text-muted italic">No responses yet.</p>
      )}
    </Card>
  );
}


/**
 * Pure-CSS distribution chart — one column per rating value, height
 * proportional to count. We deliberately don't pull in a chart
 * library for this; the data shape is small enough (≤11 buckets for
 * 0–10) that a flex row of bars renders crisper and ships zero KB
 * of extra JavaScript.
 */
function ScaleDistribution({ data }: { data: { rating: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="mt-3">
      <div className="flex items-end gap-1 h-32">
        {data.map((d) => {
          const pct = (d.count / max) * 100;
          return (
            <div
              key={d.rating}
              className="flex-1 flex flex-col items-center justify-end gap-1"
              title={`${d.rating}: ${d.count}`}
            >
              <span className="text-[10px] text-text-muted leading-none">
                {d.count}
              </span>
              <div
                className="w-full rounded-t bg-accent/80 transition-all"
                style={{ height: `${Math.max(2, pct)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1.5 px-0.5">
        {data.map((d) => (
          <div
            key={d.rating}
            className="flex-1 text-center text-[11px] text-text-muted"
          >
            {d.rating}
          </div>
        ))}
      </div>
    </div>
  );
}
